import React, { useState } from 'react';
import {
  LINK_PROMPT_HINT,
  linkChipText,
  linkChipTitle,
  linkPromptTitle,
  unlinkButtonTitle,
  unlinkConfirmText,
} from '../utils/paymentLink';

// Shared UI for linked payments, used from both the bill payments table and the
// subscription payments modal:
//   LinkPaymentPrompt — the "Also link this payment to a …?" picker, which stays
//                       open after each link so several counterparts can be
//                       added in one pass
//   PaymentLinkCell   — one chip per link, each with its own confirm-then-unlink
//
// A link is a reference, not a copy: the payment is one record that both sides
// show. Every string here comes from utils/paymentLink so the two components
// cannot drift into describing that differently, and so it can be tested.
//
// Neither uses window.confirm/alert/prompt; the confirm is the same inline
// Confirm/Cancel swap used for deletes elsewhere in the app.

const panelStyle = {
  marginTop: 12,
  marginBottom: 12,
  padding: '12px 14px',
  borderRadius: 6,
  border: '1px solid #4caf50',
  backgroundColor: '#1e2b1e',
  color: '#e0e0e0',
};

const selectStyle = {
  width: '100%',
  maxWidth: 400,
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
  color: '#e0e0e0',
};

const buttonStyle = (background) => ({
  padding: '6px 14px',
  background,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
});

const linkBtnStyle = (color) => ({
  background: 'none',
  border: 'none',
  color,
  cursor: 'pointer',
  padding: '0 6px',
});

const chipStyle = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 12,
  backgroundColor: '#2e3b2e',
  border: '1px solid #4caf50',
  color: '#c8e6c9',
  fontSize: '0.8rem',
  whiteSpace: 'nowrap',
};

const errorStyle = { color: '#f44336', marginTop: 8, marginBottom: 0 };
const successStyle = { color: '#c8e6c9', marginTop: 10, marginBottom: 0 };
const hintStyle = { color: '#a0a0a0', fontSize: '0.85rem', margin: '6px 0 0' };
const confirmTextStyle = { color: '#ddd', fontSize: '0.8rem', marginRight: 6 };

// `targetLabel` is the singular name of the OTHER side ("Subscription" / "Bill").
// `loadOptions` resolves to [{ id, label }]; `onLink(id)` performs the link and
// throws on failure. `alreadyLinkedIds` are the counterparts this payment is
// linked to already — the backend answers 409 for those, so they are shown
// disabled rather than offered again.
//
// Unlike v1 this does NOT unmount on success: the parent keeps it mounted and
// the panel offers "Link another" so one payment can be linked to several
// counterparts in a single pass. Mount it with a key of the payment id so the
// state resets when it is opened for a different payment.
export const LinkPaymentPrompt = ({
  targetLabel,
  loadOptions,
  onLink,
  onDismiss,
  alreadyLinkedIds = [],
}) => {
  const [stage, setStage] = useState('ask');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Everything this payment is linked to: what it arrived linked to, plus
  // whatever has been linked from this panel since it opened.
  const [linkedIds, setLinkedIds] = useState(() => alreadyLinkedIds.filter(Boolean));
  const [lastLinkedLabel, setLastLinkedLabel] = useState('');

  const lowerLabel = targetLabel.toLowerCase();
  const isLinked = (id) => linkedIds.includes(id);
  const selectableCount = options.filter((opt) => !isLinked(opt.id)).length;

  const readError = (err, fallback) =>
    err?.response?.data?.detail || err?.message || fallback;

  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const list = await loadOptions();
      setOptions(Array.isArray(list) ? list : []);
    } catch (err) {
      setOptions([]);
      setError(readError(err, `Could not load ${lowerLabel}s.`));
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleShowPicker = async () => {
    setStage('choose');
    setError('');
    await fetchOptions();
  };

  // "Link another" — back to the picker, with the list already loaded.
  const handleLinkAnother = () => {
    setStage('choose');
    setError('');
    setSelectedId('');
  };

  const handleLink = async () => {
    if (!selectedId) {
      setError(`Pick a ${lowerLabel} first.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onLink(selectedId);
      const chosen = options.find((opt) => opt.id === selectedId);
      setLinkedIds((prev) => (prev.includes(selectedId) ? prev : [...prev, selectedId]));
      setLastLinkedLabel(chosen?.label || `that ${lowerLabel}`);
      setSelectedId('');
      setStage('linked');
    } catch (err) {
      setError(readError(err, 'Failed to link payment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={panelStyle}>
      <strong>{linkPromptTitle(targetLabel)}</strong>
      <p style={hintStyle}>{LINK_PROMPT_HINT}</p>

      {stage === 'ask' && (
        <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
          <button type="button" onClick={handleShowPicker} style={buttonStyle('#4caf50')}>
            Link
          </button>
          <button type="button" onClick={onDismiss} style={buttonStyle('#555')}>
            Not now
          </button>
        </div>
      )}

      {stage === 'choose' && (
        <div style={{ marginTop: 10 }}>
          {loadingOptions ? (
            <p style={{ margin: 0 }}>Loading {lowerLabel}s...</p>
          ) : options.length === 0 ? (
            <p style={{ margin: 0 }}>No {lowerLabel}s found.</p>
          ) : selectableCount === 0 ? (
            <p style={{ margin: 0 }}>
              This payment is already linked to every {lowerLabel}.
            </p>
          ) : (
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={selectStyle}
              aria-label={`Select a ${lowerLabel}`}
            >
              <option value="">Select a {lowerLabel}...</option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id} disabled={isLinked(opt.id)}>
                  {isLinked(opt.id) ? `${opt.label} (already linked)` : opt.label}
                </option>
              ))}
            </select>
          )}

          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            {selectableCount > 0 && (
              <button
                type="button"
                onClick={handleLink}
                disabled={busy}
                style={{ ...buttonStyle('#4caf50'), opacity: busy ? 0.6 : 1 }}
              >
                {busy ? 'Linking...' : 'Link payment'}
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              style={buttonStyle('#555')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {stage === 'linked' && (
        <div style={{ marginTop: 10 }}>
          <p style={successStyle}>
            Linked to {lastLinkedLabel}. It is the same payment on both records.
          </p>
          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            <button type="button" onClick={handleLinkAnother} style={buttonStyle('#4caf50')}>
              Link another
            </button>
            <button type="button" onClick={onDismiss} style={buttonStyle('#555')}>
              Done
            </button>
          </div>
        </div>
      )}

      {error && <p style={errorStyle}>{error}</p>}
    </div>
  );
};

// `links` is the result of readPaymentLinks() — an empty array means the payment
// is simply not linked, which is the normal case for most rows. One chip per
// link, each unlinking only itself; `confirmingLinkId` is the link_id whose
// Confirm/Cancel swap is currently showing (null for none).
export const PaymentLinkCell = ({
  links = [],
  confirmingLinkId,
  busy,
  onRequestUnlink,
  onConfirmUnlink,
  onCancelUnlink,
  onRequestLink,
}) => {
  const linkButton = onRequestLink ? (
    <button
      type="button"
      onClick={onRequestLink}
      style={linkBtnStyle('#4caf50')}
      title="Link this payment to another record — it stays one payment"
    >
      + Link
    </button>
  ) : null;

  if (links.length === 0) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#888' }}>—</span>
        {linkButton}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {links.map((link) => (
        <span
          key={link.link_id}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
        >
          <span style={chipStyle} title={linkChipTitle(link)}>
            {linkChipText(link)}
          </span>
          {confirmingLinkId === link.link_id ? (
            <>
              <span style={confirmTextStyle}>{unlinkConfirmText(link)}</span>
              <button
                type="button"
                onClick={() => onConfirmUnlink(link.link_id)}
                disabled={busy}
                style={linkBtnStyle('#f44336')}
              >
                {busy ? 'Unlinking...' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={onCancelUnlink}
                disabled={busy}
                style={linkBtnStyle('#bbb')}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onRequestUnlink(link.link_id)}
              style={linkBtnStyle('#f44336')}
              title={unlinkButtonTitle(link)}
            >
              Unlink
            </button>
          )}
        </span>
      ))}
      {linkButton}
    </span>
  );
};
